import { useI18n } from "@/hooks/useI18n";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SEOHead } from "@/components/SEOHead";
import { 
  Target, 
  Users, 
  Leaf,
  Award,
  Globe,
  Handshake,
  LinkedinIcon,
  Shield
} from "lucide-react";
import andeanBloomLogo from "@/assets/andean-bloom-logo.png";

export default function About() {
  const { t } = useI18n();
  const [expandedBios, setExpandedBios] = useState<boolean[]>([false, false, false]);

  const values = [
    {
      icon: Handshake,
      title: t('about.transparency'),
      description: t('about.transparency.desc')
    },
    {
      icon: Award,
      title: t('about.quality'),
      description: t('about.quality.desc')
    },
    {
      icon: Shield,
      title: t('about.reliability'),
      description: t('about.reliability.desc')
    },
    {
      icon: Leaf,
      title: t('about.sustainability'),
      description: t('about.sustainability.desc')
    }
  ];

  const teamMembers = [
    {
      name: "Carlos Ruiz",
      position: "Co-Founder & CEO",
      image: "/lovable-uploads/463104be-5ee2-403e-90ca-52335b415af2.png",
      bio: "Carlos Ruiz is the Co-Founder & CEO of Andean Bloom Exports, with over seven years of experience in international trade and logistics. After working in Latin America and Australia across supply chain, logistics, and technology, he created Andean Bloom to connect Ecuadorian flower farms with global buyers. His expertise in trade, logistics, relationships, and business development allows him to build efficient export strategies while fostering strong, transparent partnerships.",
      expertise: [t('international.trade'), t('cold.chain.logistics'), t('b2b.relationships'), t('market.development')],
      linkedin: "https://www.linkedin.com/public-profile/settings?trk=d_flagship3_profile_self_view_public_profile"
    },
    {
      name: "Galo Borja",
      position: "Co-Founder & CMO",
      image: "/lovable-uploads/347cc8a8-5998-414c-b832-8cd9f12cdb47.png",
      bio: "Galo Borja is the Co-Founder & CMO of Andean Bloom Exports with a results-driven commercial lead with end-to-end B2B sales and marketing experience: he prospects and qualifies leads, builds pipeline, negotiates and closes, and activates post-sale programs for repeat business. He designs go-to-market strategies and segment-specific value propositions, manages key accounts and partnerships, and runs digital campaigns (CRM, email, LinkedIn) to generate measurable demand. He's fluent in pricing, forecasting, and unit economics (CAC, conversion, LTV), and standardizes consultative sales playbooks with clear OKRs. His approach connects farms with premium buyers, accelerating RFQs, customer satisfaction, and profitable growth.",
      expertise: ["Marketing Strategy", "Sales Management", "Market Development", "Customer Relations"],
      linkedin: "https://www.linkedin.com/in/galo-borja/"
    },
    {
      name: "Erik Jaramillo",
      position: "Co-Founder & Global Partnerships Lead",
      image: "/lovable-uploads/erik-jaramillo.png",
      bio: "Based in Ukraine, Erik supports Andean Bloom's international growth through strategic partnerships and global business development initiatives.",
      expertise: ["Strategic Partnerships", "Global Business Development", "International Relations", "Market Expansion"],
      linkedin: "https://www.linkedin.com/in/erik-jaramillo-961279192/"
    }
  ];

  const milestones = [
    { year: "2025", event: t('about.milestone.founded'), description: t('about.milestone.founded.desc') },
    { year: "2025", event: t('about.milestone.partnerships'), description: t('about.milestone.partnerships.desc') },
    { year: "2025", event: t('about.milestone.consolidation'), description: t('about.milestone.consolidation.desc') },
    { year: "2025", event: t('about.milestone.expansion'), description: t('about.milestone.expansion.desc') }
  ];

  const toggleBio = (index: number) => {
    setExpandedBios(prev => prev.map((expanded, i) => i === index ? !expanded : expanded));
  };

  const truncateBio = (bio: string, isExpanded: boolean) => {
    if (bio.length <= 150) return bio;
    return isExpanded ? bio : bio.substring(0, 150) + "...";
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-andean text-andean-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-display font-light mb-6 tracking-wide">
              Andean Bloom
            </h1>
            <p className="text-lg md:text-xl mb-8 text-andean-cream/90 font-light leading-relaxed">
              {t('about.hero.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Badge variant="secondary" className="bg-andean-cream/20 text-andean-cream border-andean-cream/30">
                <Globe className="w-4 h-4 mr-1" />
                {t('about.international')}
              </Badge>
              <Badge variant="secondary" className="bg-andean-cream/20 text-andean-cream border-andean-cream/30">
                <Target className="w-4 h-4 mr-1" />
                {t('about.b2b')}
              </Badge>
              <Badge variant="secondary" className="bg-andean-cream/20 text-andean-cream border-andean-cream/30">
                <Handshake className="w-4 h-4 mr-1" />
                {t('about.partnership')}
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-display font-light text-foreground mb-8 tracking-wide">
              {t('about.mission.title')}
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed">
              {t('about.mission.desc')}
            </p>
          </div>
        </div>
      </section>

      {/* Smart Consolidation Model Overview */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-display font-light text-foreground mb-4 tracking-wide">
              {t('about.model.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('about.model.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-andean rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-andean-cream" />
                </div>
                <CardTitle>{t('about.network.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {t('about.network.desc')}
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-andean rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-andean-cream" />
                </div>
                <CardTitle>{t('about.consolidation.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {t('about.consolidation.desc')}
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-andean rounded-full flex items-center justify-center mx-auto mb-4">
                  <Globe className="w-8 h-8 text-andean-cream" />
                </div>
                <CardTitle>{t('about.delivery.title')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  {t('about.delivery.desc')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-display font-light text-foreground mb-4 tracking-wide">
              {t('about.leadership.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('about.leadership.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {teamMembers.map((member, index) => (
              <Card key={index} className="overflow-hidden">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-1 p-6">
                    <div className="aspect-square bg-muted/50 rounded-lg mb-4 overflow-hidden">
                      <img 
                        src={member.image} 
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="text-center">
                      <h3 className="text-lg font-light text-foreground mb-1 tracking-wide">
                        {member.name}
                      </h3>
                      <p className="text-muted-foreground mb-4 text-sm">{member.position}</p>
                      <Button variant="outline" size="sm" asChild>
                        <a href={member.linkedin} target="_blank" rel="noopener noreferrer">
                          <LinkedinIcon className="w-4 h-4 mr-2" />
                          LinkedIn
                        </a>
                      </Button>
                    </div>
                  </div>
                  
                   <div className="md:col-span-2 p-6">
                     <div className="space-y-4">
                       <div>
                         <p className="text-muted-foreground leading-relaxed text-sm">
                           {truncateBio(member.bio, expandedBios[index])}
                         </p>
                         {member.bio.length > 150 && (
                           <Button 
                             variant="ghost" 
                             size="sm" 
                             onClick={() => toggleBio(index)}
                             className="mt-2 p-0 h-auto text-primary hover:text-primary/80"
                           >
                             {expandedBios[index] ? "Ver menos" : "Ver más"}
                           </Button>
                         )}
                       </div>
                       
                       <div>
                        <h4 className="font-light mb-2 text-sm tracking-wide">{t('about.expertise')}</h4>
                        <div className="flex flex-wrap gap-2">
                          {member.expertise.map((skill, skillIndex) => (
                            <Badge key={skillIndex} variant="secondary" className="text-xs">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-2xl md:text-3xl font-display font-light text-foreground mb-4 tracking-wide">
              {t('about.values.title')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('about.values.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="text-center h-full hover:shadow-andean transition-all duration-300">
                <CardHeader>
                  <div className="w-12 h-12 bg-gradient-andean rounded-lg flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-6 h-6 text-andean-cream" />
                  </div>
                  <CardTitle className="text-lg">{value.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-20 bg-gradient-andean text-andean-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-display font-light mb-6 tracking-wide">
              {t('about.partner.title')}
            </h2>
            <p className="text-xl mb-8 text-andean-cream/90">
              {t('about.partner.subtitle')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}